import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';

import { IconComponent } from '../../../../../shared/icon/icon';
import { StatusChipComponent } from '../../../../../shared/status-chip/status-chip';

/**
 * The post-submit confirmation's final state (master document: "Application
 * Submitted" with a per-product status card and one next action).
 *
 * Rendered by the wizard's `preapp-submitted` branch. FLOW-246 will pace a
 * short replay into this component — it is the final frame, and there must
 * never be a second panel beside it.
 *
 * The two pills describe the ONE enquiry, not two applications: the
 * pre-application's review status is the only lifecycle here, and a Debtors
 * Finance application exists only once Ops proceeds to products. So the codes
 * are presentation codes (`PRE_ENQUIRY_PRODUCT_META`), not backend statuses.
 *
 * The next action carries NO Debtors Finance setup button: a client never
 * self-opens a product application from the pre-application — products open
 * by Ops decision (`proceed_to_products`); the interest is `df_intent`. It DOES
 * offer "Add financial information": a SUBMITTED enquiry accepts documents
 * (PO ruling 2026-09-22, add-only), which is the path §A2.4 contemplates —
 * the Debtors Finance path opens "where the client has since supplied
 * financial statements".
 *
 * No amount, no size element (§A12, UI-DA-58-01); tone operational, no
 * celebration (DOC-38).
 */
@Component({
  selector: 'app-submitted-status',
  imports: [MatButtonModule, IconComponent, StatusChipComponent],
  templateUrl: './submitted-status.html',
  styleUrl: './submitted-status.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SubmittedStatusComponent {
  /** The client asked to be considered for Debtors Finance too (`df_intent`). */
  readonly dfIntent = input.required<boolean>();
  /** An age analysis is on the enquiry (what unlocks Debtors Finance). */
  readonly hasAgeAnalysis = input.required<boolean>();
  /** Documents shared on the enquiry. */
  readonly documentCount = input.required<number>();

  /** "Not now — just track my application": the existing dashboard route. */
  readonly dashboard = output<void>();
  /**
   * Add documents to the submitted enquiry (PO ruling 2026-09-22: a SUBMITTED
   * enquiry accepts uploads, optionally, add-only). The wizard routes it to the
   * add-documents page.
   */
  readonly addDocuments = output<void>();

  /** The Debtors Finance pill's presentation code. */
  protected readonly dfStatus = computed(() =>
    this.dfIntent() ? 'DF_INTEREST_RECORDED' : 'DF_NOT_REQUESTED',
  );

  /**
   * Which next-action card shows: the DF interest is reviewed with the enquiry;
   * or, with neither DF interest nor an age analysis, that financial
   * information could unlock more — with the honest caveat that CapX reopens
   * the enquiry if it needs it. Nothing when an age analysis was shared but the
   * client chose Kickstart Cash only: their choice stands.
   */
  protected readonly nextAction = computed<'while-you-wait' | 'unlock-more' | null>(() => {
    if (this.dfIntent()) return 'while-you-wait';
    if (!this.hasAgeAnalysis()) return 'unlock-more';
    return null;
  });
}
